import { useEffect, useRef } from "react"

import { consumer } from "@/lib/cable"
import type { ChatMessage } from "@/types"

export interface StatusEvent {
  status: "tool_call" | "tool_result"
  label?: string
}

interface ChatChannelCallbacks {
  onConnected?: () => void
  onChunk?: (content: string) => void
  onMessageComplete?: (message: ChatMessage) => void
  onError?: (message: string) => void
  onTitleUpdate?: (title: string) => void
  onStatus?: (event: StatusEvent) => void
}

export function useChatChannel(
  chatUid: string | null,
  callbacks: ChatChannelCallbacks,
) {
  const callbacksRef = useRef(callbacks)

  useEffect(() => {
    callbacksRef.current = callbacks
  })

  useEffect(() => {
    if (!chatUid) return

    const subscription = consumer.subscriptions.create(
      { channel: "ChatChannel", chat_uid: chatUid },
      {
        connected() {
          callbacksRef.current.onConnected?.()
        },
        received(data: Record<string, unknown>) {
          switch (data.type) {
            case "chunk":
              callbacksRef.current.onChunk?.(data.content as string)
              break
            case "message_complete":
              callbacksRef.current.onMessageComplete?.(
                data.message as ChatMessage,
              )
              break
            case "error":
              callbacksRef.current.onError?.(data.message as string)
              break
            case "title_update":
              callbacksRef.current.onTitleUpdate?.(data.title as string)
              break
            case "status":
              callbacksRef.current.onStatus?.({
                status: data.status as StatusEvent["status"],
                label: data.label as string | undefined,
              })
              break
          }
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [chatUid])
}
